import { SavingsRates } from 'features/accounts/helpers/savingsRates.helpers';
import { getV2AUserAgreementUrl } from 'features/v2a/receiver/helpers/V2A.helpers';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Linking, StyleSheet, View } from 'react-native';
import BodyXSBoldText from 'shared/components/vxl/typography/primary/body/BodyXSBoldText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import {
  EnvironmentContext,
  EnvironmentContextProps,
} from 'shared/providers/environment';
import theme from 'shared/styles/theme';

interface DisclosuresProps {
  savingsRates?: SavingsRates;
  isSavingsRatesUnavailable?: boolean;
  onRetrySavingsRates?: () => void;
}

const Disclosures = ({
  savingsRates,
  isSavingsRatesUnavailable = false,
  onRetrySavingsRates,
}: DisclosuresProps) => {
  const { t } = useTranslation('disclosures');
  const { currentEnv } =
    useContext<EnvironmentContextProps>(EnvironmentContext);

  const disclosures = t('disclosures.sections', {
    returnObjects: true,
  }) as { text: string }[];
  const savingsFootnote = (disclosures?.length ?? 0) + 1;

  return (
    <View style={styles.container}>
      <BodyXSBoldText style={styles.title}>
        {t('disclosures.title')}
      </BodyXSBoldText>
      <BodyXSText style={styles.heading}>
        {t('disclosures.cardIssuer')}
      </BodyXSText>
      <BodyXSText style={styles.heading}>
        *{' '}
        <BodyXSText
          style={styles.link}
          accessibilityRole="link"
          onPress={() =>
            void Linking.openURL(getV2AUserAgreementUrl(currentEnv))
          }
        >
          {t('disclosures.termsLink')}
        </BodyXSText>
      </BodyXSText>
      {disclosures?.map((section, index) => (
        <BodyXSText key={`disclosures-${index + 1}`} style={styles.heading}>
          <BodyXSText style={styles.sup}>{index + 1}</BodyXSText> {section.text}
        </BodyXSText>
      ))}
      {savingsRates ? (
        <BodyXSText style={styles.heading}>
          <BodyXSText style={styles.sup}>{savingsFootnote}</BodyXSText>{' '}
          {t('disclosures.savings.text', { ...savingsRates })}
        </BodyXSText>
      ) : null}
      {!savingsRates && isSavingsRatesUnavailable ? (
        <BodyXSText style={styles.heading}>
          <BodyXSText style={styles.sup}>{savingsFootnote}</BodyXSText>{' '}
          {t('disclosures.savings.unavailable')}{' '}
          <BodyXSBoldText
            style={styles.retry}
            accessibilityRole="button"
            onPress={onRetrySavingsRates}
          >
            {t('disclosures.savings.retry')}
          </BodyXSBoldText>
        </BodyXSText>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingVertical: theme.spacing.xl,
  },
  heading: {
    color: theme.color.text.muted,
    marginBottom: theme.spacing.m,
  },
  link: {
    color: theme.color.text.muted,
    textDecorationLine: 'underline',
  },
  retry: {
    color: theme.color.text.muted,
    textDecorationLine: 'underline',
  },
  sup: {
    color: 'unset',
    fontSize: 10,
    lineHeight: 10,
    verticalAlign: 'top',
  },
  title: {
    color: theme.color.text.muted,
    marginBottom: theme.spacing.m,
  },
});

export default Disclosures;
